import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { SessionService } from '@core/services/session';

function normalize(value: string | null | undefined): string {
  return String(value ?? '').trim().toLowerCase();
}

function isTruthyBoolean(value: unknown): boolean {
  return value === true || String(value ?? '').trim().toLowerCase() === 'true';
}

function hydratePaymentLinkSession(route: Parameters<CanActivateFn>[0]): void {
  const isReceiptRoute = route.routeConfig?.path === 'receipt/:reportparams';
  const existingReqFrom = normalize(
    sessionStorage.getItem('PaymentAccessReqFrom') || sessionStorage.getItem('ReqFrom'),
  );
  const directReqFrom =
    route.queryParamMap.get('reqFrom') ||
    route.queryParamMap.get('ReqFrom') ||
    null;
  const directStudentId =
    route.queryParamMap.get('studentId') ||
    route.queryParamMap.get('id') ||
    null;

  if (directReqFrom) {
    sessionStorage.setItem('PaymentAccessReqFrom', directReqFrom);
    sessionStorage.setItem('ReqFrom', directReqFrom);
  }

  if (directStudentId && Number(directStudentId) > 0) {
    sessionStorage.setItem('studentId', directStudentId);
    sessionStorage.setItem('paymentLinkStudentId', directStudentId);
  }

  const reportParams =
    route.paramMap.get('reportparams') ||
    route.queryParamMap.get('reportparams');
  if (!reportParams) {
    return;
  }

  sessionStorage.setItem(
    isReceiptRoute ? 'receiptReportParams' : 'paymentLinkReportParams',
    reportParams,
  );

  if (existingReqFrom === 'mailreceipt' && !isReceiptRoute) {
    return;
  }

  const defaultReqFrom = isReceiptRoute ? 'mailreceipt' : 'link';
  sessionStorage.setItem('PaymentAccessReqFrom', defaultReqFrom);
  sessionStorage.setItem('ReqFrom', defaultReqFrom);

  try {
    const decoded = atob(decodeURIComponent(reportParams));
    const parsed = JSON.parse(decoded) as {
      studentId?: string | number;
      StudentId?: string | number;
      id?: string | number;
      Id?: string | number;
      ReqFrom?: string;
      reqFrom?: string;
    };

    const rawStudentId =
      parsed?.studentId ?? parsed?.StudentId ?? parsed?.id ?? parsed?.Id ?? null;
    const studentIdMatch = String(rawStudentId ?? '').match(/\d+/);
    const studentId = studentIdMatch ? Number(studentIdMatch[0]) : NaN;
    const reqFrom = String(parsed?.ReqFrom ?? parsed?.reqFrom ?? '').trim();

    if (reqFrom) {
      sessionStorage.setItem('PaymentAccessReqFrom', reqFrom);
      sessionStorage.setItem('ReqFrom', reqFrom);
    }

    if (studentId > 0) {
      sessionStorage.setItem('studentId', String(studentId));
      sessionStorage.setItem('paymentLinkStudentId', String(studentId));
    }
  } catch {
    // ignore malformed link payload
  }
}

function buildFallbackUrl(router: Router): UrlTree {
  const reportParams = sessionStorage.getItem('paymentLinkReportParams');
  const studentId =
    sessionStorage.getItem('paymentLinkStudentId') || sessionStorage.getItem('studentId');

  if (reportParams) {
    return router.parseUrl(`/admission/paymentlink/${encodeURIComponent(reportParams)}`);
  }

  if (studentId) {
    return router.parseUrl('/admission/menu');
  }

  return router.parseUrl('/admission/student-form');
}

function buildReceiptFallbackUrl(router: Router): UrlTree {
  const reportParams =
    sessionStorage.getItem('receiptReportParams') ||
    sessionStorage.getItem('paymentLinkReportParams');

  return reportParams
    ? router.parseUrl(`/admission/receipt/${encodeURIComponent(reportParams)}`)
    : router.parseUrl('/admission/student-form');
}

export const paymentAccessGuard: CanActivateFn = (route): boolean | UrlTree => {
  const router = inject(Router);
  const session = inject(SessionService);

  hydratePaymentLinkSession(route);

  const reqFrom = normalize(
    sessionStorage.getItem('PaymentAccessReqFrom') || session.reqFrom,
  );
  const isPaymentRoute = route.routeConfig?.path === 'paymentlink';
  const isPaymentRouteWithToken = route.routeConfig?.path === 'paymentlink/:reportparams';
  const isPaymentResultRoute = route.routeConfig?.path === 'paymentlink-status/:orderId';
  const isReceiptRoute = route.routeConfig?.path === 'receipt/:reportparams';
  const hasReportParams =
    !!route.paramMap.get('reportparams') ||
    !!route.queryParamMap.get('reportparams') ||
    !!sessionStorage.getItem('paymentLinkReportParams');
  const studentId =
    route.queryParamMap.get('studentId') ||
    route.queryParamMap.get('id') ||
    sessionStorage.getItem('paymentLinkStudentId') ||
    sessionStorage.getItem('studentId');

  if (reqFrom === 'admin') {
    return router.parseUrl('/admission/menu');
  }

  if (reqFrom === 'mailreceipt') {
    return isReceiptRoute ? true : buildReceiptFallbackUrl(router);
  }

  if (reqFrom === 'link') {
    if (!isPaymentRoute && !isPaymentRouteWithToken && !isPaymentResultRoute) {
      return buildFallbackUrl(router);
    }

    return isPaymentRoute && !studentId && !hasReportParams ? buildFallbackUrl(router) : true;
  }

  if (!isTruthyBoolean(session.isPaymentEnabled)) {
    return buildFallbackUrl(router);
  }

  if (isPaymentRoute && !studentId && !hasReportParams) {
    return buildFallbackUrl(router);
  }

  return true;
};
